import { Button } from "@/components/ui/Button";
import { useLanguage } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";
import type { ChartDataItem } from "@/types";
import { ArrowsPointingOutIcon } from "@heroicons/react/24/outline";
import type { ComponentType, ReactNode, SVGProps } from "react";

export type IndexedChartDataItem = ChartDataItem & {
  chartIndex: number;
};

type SectionBadgeProps = {
  children: ReactNode;
  type?: "dark" | "light";
};

export const SectionBadge = ({
  children,
  type = "light",
}: SectionBadgeProps) => (
  <span
    className={`app-pill-section ${
      type === "dark"
        ? "text-softiq-primary"
        : "bg-white text-softiq-primary dark:bg-softiq-secondary-shade-100 dark:text-softiq-primary"
    }`}
  >
    {children}
  </span>
);

export const formatCurrency = (value: number) =>
  value.toLocaleString("cs-CZ", {
    style: "currency",
    currency: "CZK",
    maximumFractionDigits: 0,
  });

export const formatCompactCurrency = (value: number) => {
  if (Math.abs(value) >= 1_000_000) {
    return `${new Intl.NumberFormat("cs-CZ", {
      maximumFractionDigits: value >= 10_000_000 ? 0 : 1,
    }).format(value / 1_000_000)} mil.`;
  }

  if (Math.abs(value) >= 1_000) {
    return `${new Intl.NumberFormat("cs-CZ", {
      maximumFractionDigits: 0,
    }).format(value / 1_000)} tis.`;
  }

  return new Intl.NumberFormat("cs-CZ", {
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatAxisDate = (value: string) => {
  const date = new Date(value);

  return date.toLocaleDateString("cs-CZ", {
    month: "short",
    day: "numeric",
  });
};

export const getSampledTickIndices = (indices: number[], maxTicks = 6) => {
  if (indices.length <= maxTicks) {
    return indices;
  }

  const sampled = new Set<number>();
  const lastIndex = indices.length - 1;

  for (let i = 0; i < maxTicks; i += 1) {
    const sourceIndex = Math.round((i * lastIndex) / (maxTicks - 1));
    sampled.add(indices[sourceIndex]);
  }

  return Array.from(sampled);
};

export const getPointKey = (point: ChartDataItem | null) =>
  point ? `${point.offer.id}-${point.date}` : null;

type InsightItemProps = {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  text: string;
};

type MetricButtonProps = {
  label: string;
  value: ReactNode;
  isActive: boolean;
  onClick: () => void;
  secondaryLabel?: string;
  secondaryValue?: ReactNode;
  className?: string;
};

export function MetricButton({
  label,
  value,
  isActive,
  onClick,
  secondaryLabel,
  secondaryValue,
  className,
}: MetricButtonProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      active: "Aktivní",
      show: "Zobrazit",
    },
    en: {
      active: "Active",
      show: "Show",
    },
  }[language];

  return (
    <Button
      type="button"
      variant="outline"
      size={null}
      aria-pressed={isActive}
      data-active={isActive}
      onClick={onClick}
      className={cn(
        "analytics__chart_button w-full whitespace-normal items-start justify-start rounded-lg px-3 py-2.5 text-left shadow-none",
        className,
      )}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <div className="flex size-7 items-center justify-center rounded-lg border border-white/70 bg-softiq-secondary-shade-100 text-softiq-primary shadow-sm">
          <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
        </div>
        <span
          className={cn(
            "app-pill-property px-1.5 font-semibold uppercase tracking-[0.14em]",
            isActive
              ? "bg-softiq-primary text-white"
              : "bg-gray-100 text-softiq-primary dark:bg-softiq-secondary-shade-100 dark:text-softiq-primary",
          )}
        >
          {isActive ? copy.active : copy.show}
        </span>
      </div>

      <div className="mt-2 space-y-1.5">
        <span className="block card-window__inner-eyebrow dark:text-softiq-secondary-shade-300">
          {label}
        </span>
        <div className="flex flex-wrap items-end gap-x-1 gap-y-0.5 text-base font-semibold leading-none tracking-tight text-softiq-foreground sm:text-lg">
          {value}
        </div>
      </div>

      {(secondaryLabel || secondaryValue) && (
        <div className="mt-1.5 w-full rounded-md bg-softiq-primary-shade-100 p-1.5">
          <div className="flex items-end justify-between gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-foreground dark:text-softiq-primary">
              {secondaryLabel}
            </span>
            <span className="text-[11px] font-semibold leading-none text-foreground sm:text-xs dark:text-softiq-primary">
              {secondaryValue}
            </span>
          </div>
        </div>
      )}
    </Button>
  );
}
